import {
  ChangeDetectorRef,
  Component,
  inject,
  Injectable,
  InjectionToken,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonService } from '../services/common/common.service';
import { HomeComponent } from '../home/home/home.component';
import { DashboardService } from '../services/dashboard/dashboard.service';
import { DashboardStateService } from '../services/dashboard/dashboard-state.service';
import { DashboardWidgets, Widget } from 'src/models/interfaces/dashboard/dashboard.interface';
import { LoadingService } from '../services/loading/loading.service';
import { FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { getDateBrasil } from 'src/utils/getDate';
import {
  DateFilterFn,
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDateRangePicker,
  MatDateRangeSelectionStrategy
} from '@angular/material/datepicker';
import { ValidatorService } from '../services/validators/errors-validators.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { DatePipe, formatDate } from '@angular/common';
import { DATE_FORMAT } from 'src/models/interfaces/date-format.interface';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { DashboardLoadingKeys } from 'src/models/keys/dashboard/dashboard.keys';
import {
  BrazilianDateAdapter,
  DayRangeSelectionStrategy,
  ToggleableRangeSelectionStrategy
} from 'src/utils/rangeDateHandler';


import { PrestacaoContaMultiService } from '../services/prestacao-conta-multi/prestacao-conta-multi.service';


@Component({
  selector: 'spe-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: BrazilianDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT },
    DayRangeSelectionStrategy,
    DefaultMatCalendarRangeStrategy,
    ToggleableRangeSelectionStrategy,
    // Use the toggleable one as the active strategy
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useExisting: ToggleableRangeSelectionStrategy
    },
    DatePipe
  ],
  standalone: false
})
export class DashboardComponent {
  // which side the user is currently editing in the UI
  activePart: 'start' | 'end' = 'start';
  // Recomputed when start changes; type is the adapter's D (Date or Moment)
  public filterFormGroup = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    statusPrestacao: new FormControl<string | null>('')
  });
  minEndDate: any | null = null;
  maxEndDate: any | null = null;
  private startSub?: Subscription;
  private endSub?: Subscription;
  @ViewChild('pickerRange') pickerRange!: MatDateRangePicker<Date>;
  public readonly validatorErrors = this.validatorService.validatorErrors;
  public loading$?: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();

  public statusPrestacao = [{ label: 'Todas', value: '' }];

  get kpiTotalDespesasValor(): number {
    return this.dashboardState.kpiTotalDespesasValor;
  }

  get kpiTotalDespesasTitulo(): string {
    return this.dashboardState.kpiTotalDespesasTitulo;
  }

  get drillDownFilterTitle(): string {
    return this.dashboardState.drillDownFilterTitle;
  }

  get drillDownFilter(): string {
    return this.dashboardState.drillDownFilter;
  }

  constructor(
    private homeComponent: HomeComponent,
    public commonService: CommonService,
    private loadingService: LoadingService,
    public readonly validatorService: ValidatorService,
    private toggleStrategy: ToggleableRangeSelectionStrategy<Date>,
    private readonly prestacaoContaMultiService: PrestacaoContaMultiService,
    private dateAdapter: DateAdapter<any>,
    public cdr: ChangeDetectorRef,
    private dashboardState: DashboardStateService
  ) {}

  ngOnInit(): void {
    this.loading$ = this.loadingService.create(DashboardLoadingKeys.Default).loading$;
    this.homeComponent.setNewVariables('Dashboard', '', 'cartoes/multi');
    this.startAndEndSub();
    this.getStatusPrestacao();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getStatusPrestacao() {
    this.prestacaoContaMultiService
      .listarStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.statusPrestacao.push(...data.status.map((i) => ({ value: i.codigo, label: i.descricao })));
        },
        error: () => {}
      });
  }

  public setKpiTotalDespesas = (value: number, titulo: string) => {
    this.dashboardState.setKpiTotalDespesas(value, titulo);
  };

  public setDrillDownFilter = (filter: string, title: string) => {

    this.dashboardState.setDrillDownFilter(filter, title);
    this.commonService.autoScroll();

  };

  get startDate(): string | null {
    const value = this.filterFormGroup.get('startDate')?.value;
    return value ? formatDate(value, 'yyyy-MM-dd', 'en-US') : null;
  }
  get endDate(): string | null {
    const value = this.filterFormGroup.get('endDate')?.value;
    return value ? formatDate(value, 'yyyy-MM-dd', 'en-US') : null;
  }

  get statusPrestacaoFilter(): string | null {
    const value = this.filterFormGroup.get('statusPrestacao')?.value;
    return value || null;
  }

  startAndEndSub = () => {
    const startCtrl = this.filterFormGroup.get('startDate')!;
    const endCtrl = this.filterFormGroup.get('endDate')!;

    this.startSub = startCtrl.valueChanges.subscribe((start) => {
      this.activePart = 'start';

      const minDaysDifference = 0;
      const maxDaysDifference = 90;

      this.minEndDate = start ? this.dateAdapter.addCalendarDays(start, minDaysDifference) : null;
      this.maxEndDate = start ? this.dateAdapter.addCalendarDays(start, maxDaysDifference) : null;

      this.cdr.markForCheck();
      if (start) this.activePart = 'end';

      // Só reabre para escolher a data final se o usuário já abriu o calendário.
      // Evita abrir sozinho no load (overlay no canto superior com o campo ainda hidden).
      const wasOpen = !!this.pickerRange?.opened;
      if (wasOpen && start) {
        this.pickerRange.close();

        setTimeout(() => {
          this.pickerRange.startAt = this.minEndDate;
          this.pickerRange.open();
        }, 0);
        setTimeout(() => {
          this.pickerRange.startAt = null;
        });
      }
    });

    this.endSub = endCtrl.valueChanges.subscribe((end) => {
      if (end) {
        this.activePart = 'start';
      }
    });

    const localTime = getDateBrasil();
    const today = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate());
    // Calcula 90 dias antes
    const ninetyDaysEarlier = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate() - 90);
    startCtrl.setValue(ninetyDaysEarlier, { emitEvent: false });
    endCtrl.setValue(today, { emitEvent: false });
    this.minEndDate = this.dateAdapter.addCalendarDays(ninetyDaysEarlier, 0);
    this.maxEndDate = this.dateAdapter.addCalendarDays(ninetyDaysEarlier, 90);
  };

  /**
   * Verifica se a data está dentro do intervalo permitido (hoje até 90 dias)
   */
  dateRangeValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value) {
      return null; // Se não houver valor, deixa o required cuidar disso
    }

    const date = new Date(control.value);
    const localTime = getDateBrasil();
    const today = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate());

    // Calcula 90 dias antes
    const ninetyDaysEarlier = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate() - 90);

    // Verifica se a data é posterior a hoje
    if (date > today) {
      return { dateAfterToday: true };
    }

    // Verifica se a data é anterior a 90 dias
    if (date < ninetyDaysEarlier) {
      return { dateBeforeNinetyDays: true };
    }

    return null;
  }

  rangeFilter: DateFilterFn<Date | null> = (date: Date | null) => {
    if (!date) return false;

    // Hoje (normalizado) via sua função getDateBrasil()
    const localTime = getDateBrasil();
    const today = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate());
    const ninetyDaysEarlier = new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate() - 90);
    // Regra 1: só hoje em diante e menos de 31 dias
    if (date > today) {
      return false;
    }

    if (date < ninetyDaysEarlier) {
      return false;
    }
    // Regra 2: ao escolher FIM, respeitar
    // CASO PEDIDO COMPLETO:
    // fim mínimo(minEndDate) = início + 13 e fim máximo(maxEndDate) = início + 30
    // CASO PEDIDO COMPLEMENTAR:
    // fim mínimo(minEndDate) = início + 0 e fim máximo(maxEndDate) = início + 30
    if (this.activePart === 'end' && this.minEndDate) {
      return date >= this.minEndDate && date <= this.maxEndDate;
    }

    return true;
  };

  dateRangeErrorStateMatcher = {
    isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
      if (!control || !form) return false;

      const parent = control.parent;
      if (!parent) return false;

      const start = parent.get('startDate')?.value;
      const end = parent.get('endDate')?.value;

      // Só marca como inválido se ambos são escolhidos E se pelo menos 1 é invalido
      const bothSelected = !!start && !!end;
      const controlInvalid = control.invalid && (control.dirty || control.touched);

      return bothSelected && controlInvalid;
    }
  };
}
